import React from 'react';
import { Link } from 'react-router-dom';
import { useReveal } from '../lib/useReveal';

const TYPES = [
  {
    name: 'Anniversaries & Birthdays',
    body: 'A romantic or celebratory journey featuring champagne, private dining, chef’s table seating, personalised menus, and elegant surprises.',
    image: '/assets/photos/romantic-table.jpg',
  },
  {
    name: 'Proposals',
    body: 'A discreetly planned culinary experience ending in a beautiful proposal moment, with optional flowers, photographer, champagne, and private room.',
    image: '/assets/photos/golden-digestif.jpg',
  },
  {
    name: 'Luxury Travellers',
    body: 'An insider introduction to the destination’s best food, wine, produce and dining culture, hosted from arrival to final course.',
    image: '/assets/photos/champagne-rooftop.jpg',
  },
  {
    name: 'VIP Guests',
    body: 'A polished hosting experience for important visitors, executives, investors or family guests — designed to impress without ever feeling forced.',
    image: '/assets/photos/sommelier-decanting.jpg',
  },
  {
    name: 'Private Groups',
    body: 'An elevated food and wine experience for small groups who want a refined alternative to standard dining and predictable venues.',
    image: '/assets/photos/private-dining.jpg',
  },
];

export default function PrivateExperiences() {
  const ref = useReveal();
  return (
    <div ref={ref} data-testid="private-experiences-page" className="pt-36 sm:pt-44 lg:pt-56">
      <section className="max-w-[1200px] mx-auto px-5 sm:px-6 lg:px-12 pb-16 lg:pb-24">
        <div className="max-w-3xl reveal">
          <div className="tdt-eyebrow mb-5">Private Experiences</div>
          <h1 className="font-serif font-light text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[1.02] text-[color:var(--tdt-ink)] mb-8">
            Private Culinary Experiences.
          </h1>
          <p className="text-base sm:text-lg lg:text-lg leading-relaxed text-[color:var(--tdt-muted)] mb-5">
            For those seeking something personal, elegant and memorable, Tour de Taste creates private food and wine experiences tailored to each guest.
          </p>
          <p className="text-base sm:text-lg lg:text-lg leading-relaxed text-[color:var(--tdt-muted)]">
            Every detail is considered — from the first glass to the final course. We curate the venues, makers, chefs, pairings, transfers and finishing touches to create an experience that feels effortless and deeply personal.
          </p>
        </div>
      </section>

      <section className="bg-[color:var(--tdt-cream)] py-14 sm:py-18 lg:py-28">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-6 lg:px-12 space-y-24 lg:space-y-32">
          {TYPES.map((t, i) => (
            <div
              key={t.name}
              data-testid={`private-type-${i}`}
              className={`grid lg:grid-cols-12 gap-10 lg:gap-16 items-center ${i % 2 === 1 ? 'lg:[&>div:first-child]:order-2' : ''}`}
            >
              <div className="lg:col-span-7 reveal media-card relative aspect-[4/3]">
                <img src={t.image} alt={t.name} className="absolute inset-0 w-full h-full object-cover" />
              </div>
              <div className="lg:col-span-5 reveal">
                <div className="tdt-eyebrow mb-4">{String(i + 1).padStart(2, '0')}</div>
                <h2 className="font-serif font-light text-2xl sm:text-2xl sm:text-3xl md:text-4xl lg:text-5xl leading-[1.05] text-[color:var(--tdt-ink)] mb-5">{t.name}</h2>
                <div className="hr-gold mb-6" />
                <p className="text-[15px] sm:text-base lg:text-base leading-relaxed text-[color:var(--tdt-muted)]">{t.body}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-[color:var(--tdt-ivory)] py-16 sm:py-14 sm:py-18 lg:py-32 text-center">
        <div className="max-w-[820px] mx-auto px-5 sm:px-6 lg:px-12 reveal">
          <h2 className="font-serif font-light text-3xl sm:text-4xl md:text-5xl leading-[1.05] text-[color:var(--tdt-ink)] mb-6">
            Begin a private experience.
          </h2>
          <p className="text-[color:var(--tdt-muted)] mb-10">Tell us about your occasion. We will curate the rest.</p>
          <Link to="/enquire" data-testid="private-enquire-btn" className="btn-pill btn-gold">Enquire Privately</Link>
        </div>
      </section>
    </div>
  );
}
